import { ServiceDeskConfig } from "../Startup/serviceDeskConfig";
import { HttpTrackingConfig } from "../../ShellCommon/logger/http-interceptor";
import { LoggingService } from "./loggingSvc";
import { SdControllerUtilities } from "./sd-controller/sd-controller-utilities";

// Note: these two types (DomainDataType and DomainDataBulkResponse) both need to be kept in sync. The interface is sugar
//  for consumers, hopefully in the future we can have strongly typed signatures on the bulk response as well.
/**
 * The valid types of domain data that can be requested.
 */
export type DomainDataType = "configs" | "locales" | "modes" | "sites" | "srqueues" | "subjects" | "tiers";

/**
 * The possible shape for a domain data bulk request. Only the supplied types will be populated on the returned response.
 */
export interface DomainDataBulkResponse {
  configs?: any;
  locales?: any;
  modes?: any;
  sites?: any;
  srqueues?: any;
  subjects?: any;
  tiers?: any;
}

/**
 * The service responsible for retrieving domain data
 */
export interface DomainDataService {
  /**
   * Gets domain data for for a single data type. The IHttpPromiseCallbackArg is automatically
   *  unwrapped, showing only the message content for both success and failure promise callbacks.
   * @param {string} domainType - The domain type to fetch.
   */
  getData(domainType: DomainDataType): ng.IPromise<any>;

  /**
   * Gets domain data for multiple data types at a time. Unwraps response .data into an
   *  object keyes by domainType. Only the first failure is returned to the caller
   *  if any of the requests fail. If you invoke with params "foo" and "bar", you will
   *  receive an object with keys "foo" and "bar" with data unwrapped (i.e. not HTTP callback args)
   *  in values.
   * @param {...string[]}  domainTypes - The domain types to fetch.
   */
  getDataBulk(...domainTypes: DomainDataType[]): ng.IPromise<DomainDataBulkResponse>;

  /**
   * Gets canned messages for a single language. The IHttpPromiseCallbackArg is automatically
   *  unwrapped, showing only the message content for both success and failure promise callbacks.
   * @param {string} language - The language to fetch canned messages for.
   */
  getCannedMessages(language: string, lob?: string): ng.IPromise<any>;

  /**
   * Gets the welcome and thankyou messages configured for comm agents
   * @returns ng
   */
  getGreetingMessages(): ng.IPromise<any>;
}

export interface ASDAgentDetails {
  lob: string;
  agentPuid: string;
  vendorSiteId: string;
  cannedMsgScope: string;
}

// tslint:disable-next-line:variable-name
export let ASDAgentDetailsKey = "ASDAgentDetails";

export class DomainDataServiceImplementation implements DomainDataService {
  private agentDetails: ASDAgentDetails;

  // @ngInject
  constructor(
    private $http: ng.IHttpService,
    private $q: ng.IQService,
    private loggingService: LoggingService,
    private sdControllerUtilities: SdControllerUtilities,
    private sdConfig: ServiceDeskConfig
  ) {
    // get asd agent details from local storage this is a temp fix to make sure we can pass lob and etc after full agent details
    // is received from ServiceDesk we would use this userprofileService Agent object itself
    try {
      let agentDetailsString = window.localStorage.getItem(ASDAgentDetailsKey);
      this.agentDetails = <ASDAgentDetails>JSON.parse(agentDetailsString);
    } catch (error) {
      loggingService.logWarning("Unable to read ASDAgent Details");
    }
  }

  private getDataInternal(domainType: string): ng.IPromise<any> {
    let config: HttpTrackingConfig = this.sdControllerUtilities.getParametersForUserCacheBusting();
    config.dependencyName = "ServiceDesk.SDController";
    switch (domainType) {
      case "locales":
        config.operationName = "GetLocales";
        config.dependencyOperationName = "DomainData.GetLocales";
        break;
      case "modes":
        config.operationName = "GetModes";
        config.dependencyOperationName = "DomainData.GetModes";
        break;
      case "sites":
        config.operationName = "GetSites";
        config.dependencyOperationName = "DomainData.GetSites";
        break;
      case "subjects":
        config.operationName = "GetSubjects";
        config.dependencyOperationName = "DomainData.GetSubjects";
        break;
      case "tiers":
        config.operationName = "GetTiers";
        config.dependencyOperationName = "DomainData.GetTiers";
        break;
      default:
        // set default value
        if (domainType) {
          let domainValues = domainType.split("?");
          config.operationName = domainValues[0];
          config.dependencyOperationName = `DomainData.${domainValues[0]}`;
        }
        this.loggingService.logWarning("Unknown domainType: " + domainType);
    }
    return this.$http.get(`${this.sdConfig.sdBaseUrl}SDController/api/DomainData/${domainType}`, config);
  }

  getData(domainType: string): ng.IPromise<any> {
    return this.getDataInternal(domainType).then(
      (success: ng.IHttpPromiseCallbackArg<any>) => {
        return success.data;
      },
      (failure: ng.IHttpPromiseCallbackArg<any>) => {
        return failure.data;
      }
    );
  }

  getGreetingMessages(): ng.IPromise<any> {
    let config: HttpTrackingConfig = this.sdControllerUtilities.getParametersForUserCacheBusting();
    config.dependencyName = "ServiceDesk.SDController";
    config.operationName = "GetGreetingMessages";
    config.dependencyOperationName = "DomainData.GetGreetingMessages";
    return this.$http.get(`${this.sdConfig.sdBaseUrl}SDController/api/DomainData/GreetingMessages/`, config).then(
      (success) => {
        return success.data;
      },
      (failure) => {
        return failure.data;
      }
    );
  }

  getDataBulk(...domainTypes: DomainDataType[]): ng.IPromise<DomainDataBulkResponse> {
    if (!domainTypes || !Array.isArray(domainTypes) || domainTypes.length === 0) {
      throw new Error("You should invoke getDataBulk with an array of domainType with length > 0.");
    }

    let fetchers: ng.IPromise<any>[] = [];
    // We cross the type boundaries between our input list of strings and output type here. This is because
    //  TypeScript doesn"t support our "string enum" type in indexers.
    let successfulReturnObject: { [domainDataType: string]: any } = {};
    domainTypes.forEach((domain: string) => {
      fetchers.push(
        this.getDataInternal(domain).then((response: ng.IHttpPromiseCallbackArg<any>) => {
          successfulReturnObject[domain] = response.data;
        })
      );
    });

    return this.$q.all(fetchers).then((data: ng.IPromise<any>[]) => {
      // Use our specially keyed response object instead of all"s default response.
      return successfulReturnObject;
    });
  }

  getCannedMessages(language: string, lob?: string): ng.IPromise<any> {
    let config: HttpTrackingConfig = this.sdControllerUtilities.getParametersForUserCacheBusting();
    config.dependencyName = "ServiceDesk.SDController";
    config.operationName = "GetCannedmessagesByLob";
    config.dependencyOperationName = "DomainData.GetCannedmessagesByLob";
    let inputParams = lob
      ? lob + "/" + language
      : this.agentDetails && this.agentDetails.cannedMsgScope
      ? this.agentDetails.cannedMsgScope + "/" + language
      : "nolob" + "/" + language;
    return this.$http
      .get(`${this.sdConfig.sdBaseUrl}SDController/api/DomainData/cannedmessages/${inputParams}`, config)
      .then(
        (success) => {
          return success.data;
        },
        (failure) => {
          return failure.data;
        }
      );
  }
}
